// ---------------------------------------------------------
// Datei: ProjectsScreen.tsx
// Zweck: Hauptseite für die Anzeige und Verwaltung von Projekten
// Besonderheiten:
// - Verwendet MyProjectsGrid für Anzeige der Projekte mit Heute-Filter
// - Modal zum Erstellen neuer Projekte
// - Header mit Benutzerinfo und Logout
// - ThemeToggle für Light/Dark Mode (Sprint 2 Task 1)
// - Overflow-Menu für sekundäre Actions (Logout)
// - Suchleiste zum Filtern der Deals (NEU)
// ---------------------------------------------------------

import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { resolveOwnersByEmailAsync as resolveHubSpotOwnerIdsByEmail } from "../services/api/hubspot";
import { useDealsStore } from "../store/dealsStore";
import ViewToggle from "../components/ViewToggle";
import AppHeader from "../components/AppHeader";
import ProjectScopeTabs from "../components/ProjectScopeTabs";
import ProjectMilestoneTimeline from "../components/ProjectMilestoneTimeline";
import DealStatusFilterBar, { DealStageFilter, InterneFreigabeFilter, NacharbeitenFilter, WartungFilter } from "../components/DealStatusFilterBar";
import { useToast } from "../contexts/ToastContext";
import { applyDealStageToProjectStatusPhases, dealMatchesStageLabel } from "../services/hubspotDealMapper";
import {
  getDefaultProjectScope,
  matchesProjectScope,
  persistProjectScope,
  PersistedProjectScope,
} from "../utils/projectScope";

function getInterneFreigabeStatus(deal: any): "draft" | "in_review" | "ready" | "approved" | null {
  const direct = deal?.project_status?.projektdokumentationApprovalStatus;
  if (direct === "draft" || direct === "in_review" || direct === "ready" || direct === "approved") {
    return direct;
  }

  const projektdokumentationPhase = (deal?.project_status?.phases || []).find(
    (phase: any) => phase?.phaseKey === "projektdokumentation"
  );

  if (!projektdokumentationPhase) {
    return null;
  }

  // Legacy fallback: ältere Snapshots hatten das Approval-Statusfeld noch nicht.
  // Wenn approvedByEmail fehlt, ist "completed" sehr wahrscheinlich "ready".
  if (projektdokumentationPhase.status === "completed") {
    return projektdokumentationPhase.approvedByEmail ? "approved" : "ready";
  }

  if (projektdokumentationPhase.status === "open") {
    return "draft";
  }

  // Legacy-Heuristik: ältere Snapshots ohne explizites Approval-Statusfeld.
  if (projektdokumentationPhase.status === "in_progress") {
    const completion = Number(projektdokumentationPhase.completionPercent || 0);
    const missingRequired = Number(projektdokumentationPhase.missingRequiredCount || 0);
    if (completion >= 100 && missingRequired === 0) {
      return "ready";
    }
    if (completion > 0) {
      return "in_review";
    }
  }

  return null;
}

const DEAL_STAGE_FILTER_LABELS: Record<Exclude<DealStageFilter, 'all'>, string> = {
  'kunde-wartet': 'Kunde wartet',
  'projekt-angenommen': 'Projekt Angenommen',
  'projekt-abgeschlossen': 'Projekt Abgeschlossen',
  'durchfuehrung-abgeschlossen': 'Durchführung abgeschlossen',
  waiting: 'waiting',
  dubletten: 'Dubletten',
  'anfrage-zum-kennenlernen-verschicken': 'Anfrage zum Kennenlernen verschicken',
};

const ProjectsScreen: React. FC = () => {
  // === State-Definitionen ===
  const [scope, setScope] = useState<PersistedProjectScope>(() => getDefaultProjectScope());
  const [dealStageFilter, setDealStageFilter] = useState<DealStageFilter>('all');
  const [nacharbeitenFilter, setNacharbeitenFilter] = useState<NacharbeitenFilter>('all');
  const [wartungFilter, setWartungFilter] = useState<WartungFilter>('all');
  const [interneFreigabeFilter, setInterneFreigabeFilter] = useState<InterneFreigabeFilter>('all');
  // NEU: State für Suchleiste
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [hubspotOwnerIds, setHubspotOwnerIds] = useState<string[]>([]);
  const skipNextFilterFetchRef = useRef(false);

  // Auth-Context für Benutzerinfo und Logout
  const { user, isAuthenticated, logout } = useAuth();
  const { showToast } = useToast();

  // Navigation-Hook für Projekt-Klick
  const navigate = useNavigate();

  // Deals aus dem persistierten Store (offline-fähig)
  const {
    deals,
    searchResults,
    isLoading: dealsLoading,
    isLoadingMore,
    hasMore,
    error: dealsError,
    loadMoreError,
    isSearching,
    searchError,
    fetchDeals,
    searchDealsIntoCache,
    clearLoadMoreError,
  } = useDealsStore();

  // Gefilterte Deals basierend auf Suchbegriff
  const filteredDeals = useMemo(() => {
    const sourceDeals = searchQuery.trim().length > 0 ? (searchResults || []) : deals;
    if (searchQuery.trim().length > 0) {
      return sourceDeals;
    }

    return sourceDeals
      .filter((deal) => matchesProjectScope(deal, scope, user, { hubspotOwnerIds }))
      .filter((deal) => {
        if (dealStageFilter === 'all') return true;
        return dealMatchesStageLabel(deal, DEAL_STAGE_FILTER_LABELS[dealStageFilter]);
      })
      .filter((deal) => {
        if (nacharbeitenFilter === 'all') return true;
        const hasOpenNacharbeiten = Boolean(deal.project_status?.nacharbeiten?.hasOpenNacharbeiten);
        return nacharbeitenFilter === 'yes' ? hasOpenNacharbeiten : !hasOpenNacharbeiten;
      })
      .filter((deal) => {
        if (wartungFilter === 'all') return true;
        const hasOpenWartung = Boolean(deal.project_status?.wartung?.hasOpenWartung);
        return wartungFilter === 'yes' ? hasOpenWartung : !hasOpenWartung;
      })
      .filter((deal) => {
        if (interneFreigabeFilter === 'all') return true;
        const status = getInterneFreigabeStatus(deal);
        if (interneFreigabeFilter === 'empty_or_draft') {
          return status === null || status === 'draft';
        }
        return status === interneFreigabeFilter;
      });
  }, [deals, dealStageFilter, hubspotOwnerIds, interneFreigabeFilter, nacharbeitenFilter, wartungFilter, scope, searchQuery, searchResults, user]);

  // === Navigation-Handler ===
  const handleDealClick = (dealId: string) => {
    navigate(`/projects/${dealId}`);
  };

  useEffect(() => {
    const email = user?.email?.trim();
    if (!email) {
      setHubspotOwnerIds([]);
      return;
    }

    let cancelled = false;

    const load = async () => {
      if (!navigator.onLine) return;
      const ownerIds = await resolveHubSpotOwnerIdsByEmail(email);
      if (cancelled) return;
      setHubspotOwnerIds(ownerIds);
    };

    void load();

    window.addEventListener('online', load);
    return () => {
      cancelled = true;
      window.removeEventListener('online', load);
    };
  }, [user?.email]);

  useEffect(() => {
    if (!loadMoreError) return;
    showToast('warning', 'Weitere Projekte konnten nicht nachgeladen werden. Die vorhandenen Projekte bleiben nutzbar.', 5000);
    clearLoadMoreError();
  }, [clearLoadMoreError, loadMoreError, showToast]);

  useEffect(() => {
    if (!searchError) return;
    showToast('warning', searchError, 5000);
  }, [searchError, showToast]);

  useEffect(() => {
    const handleRefresh = () => {
      if (!isAuthenticated) return;
      void fetchDeals({
        forceRefresh: true,
        cacheOwnerKey: user?.email || user?.id || null,
      });
    };

    window.addEventListener('project-status-refresh', handleRefresh);
    return () => window.removeEventListener('project-status-refresh', handleRefresh);
  }, [fetchDeals, isAuthenticated, user?.email, user?.id]);

  // Deal-Status/Nacharbeiten/Interne Freigabe sind reine Anzeige-Filter (siehe filteredDeals
  // oben) - sie lösen bewusst KEINEN Server-Request aus. Es wird nur einmal der volle,
  // ungefilterte Bestand geladen; der Hintergrund-Sync in dealsStore hält ihn unabhängig
  // vom gewählten Filter aktuell (siehe Bugreport: Filterwechsel darf keinen Reload triggern).
  useEffect(() => {
    // Ohne bestätigten Login weder Deals nachladen noch anzeigen (siehe Render-Guard
    // unten) - unabhängig davon, ob AuthGuard (z.B. per Dev-Testflag) gerade
    // umgangen wird oder der Login-Vorgang schlicht noch läuft.
    if (!isAuthenticated) return;
    if (searchQuery.trim().length > 0) return;
    if (skipNextFilterFetchRef.current) {
      skipNextFilterFetchRef.current = false;
      return;
    }
    void fetchDeals({
      cacheOwnerKey: user?.email || user?.id || null,
      restoreUnfilteredFromCache: true,
    });
  }, [fetchDeals, isAuthenticated, searchQuery, user?.email, user?.id]);

  useEffect(() => {
    if (!isAuthenticated) return;
    const q = searchQuery.trim();
    if (q.length < 3) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      void searchDealsIntoCache(q, {
        cacheOwnerKey: user?.email || user?.id || null,
      });
    }, 350);

    return () => window.clearTimeout(timeoutId);
  }, [isAuthenticated, searchDealsIntoCache, searchQuery, user?.email, user?.id]);

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
  };

  const handleScopeChange = (value: PersistedProjectScope) => {
    const hadSearch = searchQuery.trim().length > 0;
    setScope(value);
    persistProjectScope(value);
    setSearchQuery('');
    if (hadSearch) {
      setDealStageFilter('all');
      setNacharbeitenFilter('all');
      setWartungFilter('all');
      setInterneFreigabeFilter('all');
    }
  };

  // Ohne bestätigten Login weder Deals fetchen (siehe Effekte oben) noch anzeigen -
  // verhindert, dass Deals eines vorherigen/anderen Kontos sichtbar bleiben, während
  // ein neuer Login-Vorgang noch läuft (z.B. AuthGuard per Dev-Testflag umgangen, oder
  // die Google-Session wurde direkt bei Google beendet, ohne dass die App das über
  // einen eigenen Logout-Event mitbekommen hätte).
  if (!isAuthenticated) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-transparent">
      <AppHeader
        title="360Volt Projekte"
        actions={<ViewToggle />}
        menuContent={(
          <>
            <div className="sm:hidden border-b border-slate-200 px-2 py-2">
              <ViewToggle />
            </div>
            <button
              onClick={logout}
              className="mt-1 flex min-h-[48px] w-full items-center rounded-xl px-3 py-3 text-left text-sm text-red-600 transition hover:bg-red-50"
            >
              <svg className="mr-3 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Abmelden
            </button>
          </>
        )}
      />
      <ProjectScopeTabs
        value={searchQuery.trim().length > 0 ? 'search' : scope}
        onChange={(value) => {
          if (value === 'search') return;
          handleScopeChange(value);
        }}
        searchValue={searchQuery}
        onSearchChange={handleSearchChange}
        onSearchSubmit={async () => {
          const q = searchQuery.trim();
          if (q.length >= 3) {
            await searchDealsIntoCache(q);
          }
        }}
        onClearSearch={() => {
          setSearchQuery('');
        }}
        searchLoading={isSearching}
      />

      {/* Hauptinhalt */}
      <main className="max-w-7xl mx-auto px-4 py-6 sm:px-6 pb-[calc(env(safe-area-inset-bottom)+80px)]">
        <div className="mb-6">
          <DealStatusFilterBar
            dealStageFilter={dealStageFilter}
            nacharbeitenFilter={nacharbeitenFilter}
            wartungFilter={wartungFilter}
            interneFreigabeFilter={interneFreigabeFilter}
            onDealStageFilterChange={setDealStageFilter}
            onNacharbeitenFilterChange={setNacharbeitenFilter}
            onWartungFilterChange={setWartungFilter}
            onInterneFreigabeFilterChange={setInterneFreigabeFilter}
            onReset={() => {
              skipNextFilterFetchRef.current = true;
              setDealStageFilter('all');
              setNacharbeitenFilter('all');
              setWartungFilter('all');
              setInterneFreigabeFilter('all');
              void fetchDeals({
                cacheOwnerKey: user?.email || user?.id || null,
                restoreUnfilteredFromCache: true,
              });
            }}
            onRefresh={() => {
              void fetchDeals({
                forceRefresh: true,
                cacheOwnerKey: user?.email || user?.id || null,
              });
            }}
            disabled={searchQuery.trim().length > 0}
          />
        </div>
        {/* Fehleranzeige */}
        {dealsError && (
          <div className="bg-red-50 border border-red-200 text-red-800 rounded-md p-4 mb-6">
            {dealsError}
          </div>
        )}

        <div className="mb-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Projekte
          </h2>
        </div>

        <section className="mb-8" data-testid="project-list">
          <div className="mb-4">
            <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{filteredDeals.length} Projekte</p>
            {(isLoadingMore || hasMore) && (
              <p className="text-xs text-slate-500 dark:text-slate-400">Lade weitere Projekte nach...</p>
            )}
            {searchQuery.trim().length >= 3 && isSearching && (
              <p className="text-xs text-slate-500 dark:text-slate-400">Weitere Suchergebnisse werden vom Server geladen...</p>
            )}
          </div>
          {dealsLoading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
            </div>
          ) : filteredDeals.length === 0 ? (
            <div className="text-center py-12">
              <svg
                className="w-16 h-16 mx-auto mb-4 text-gray-300 dark:text-gray-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="1.5"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <p className="text-gray-500 dark:text-gray-400">
                {searchQuery 
                  ? `Keine Projekte für "${searchQuery}" gefunden. `
                  : 'Keine Deals gefunden.'}
              </p>
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="mt-4 text-primary-600 dark:text-primary-400 hover:underline"
                >
                  Suche zurücksetzen
                </button>
              )}
            </div>
          ) : (
            <div className="grid min-w-0 gap-6 pb-6 md:grid-cols-2 lg:grid-cols-3" data-testid="project-grid">
              {filteredDeals.map((deal) => {
                const effectiveProjectStatusPhases = applyDealStageToProjectStatusPhases(deal.project_status?.phases, deal);
                return (
                  <button
                    key={deal.id}
                    data-testid="project-item"
                    data-deal-id={deal.id}
                    onClick={() => handleDealClick(deal.id)}
                    className="w-full min-w-0 cursor-pointer overflow-hidden rounded-2xl border border-black/10 bg-white/95 p-4 text-left shadow-md transition-all hover:border-[var(--color-primary)] hover:shadow-lg active:scale-95 dark:border-white/10 dark:bg-gray-800/95 sm:p-6"
                  >
                    <div className="mb-4 flex items-start justify-between">
                      <div className="min-w-0 flex-1">
                        <div className="mb-2 flex min-w-0 items-start justify-between gap-3">
                          <h3 className="min-w-0 break-words text-lg font-semibold leading-snug text-primary-700 line-clamp-2 dark:text-primary-300 sm:truncate">
                            {deal.dealname || (
                              <span className="italic text-gray-400">
                                Kein Name
                              </span>
                            )}
                          </h3>
                          {deal.project_status?.nacharbeiten?.hasOpenNacharbeiten && (
                            <span className="shrink-0 rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-800 dark:bg-amber-500/15 dark:text-amber-200">
                              {deal.project_status.nacharbeiten.openNacharbeitenCount} Nacharbeit{deal.project_status.nacharbeiten.openNacharbeitenCount === 1 ? '' : 'en'}
                            </span>
                          )}
                          {deal.project_status?.wartung?.hasOpenWartung && (
                            <span className="shrink-0 rounded-full bg-sky-100 px-3 py-1 text-xs font-semibold text-sky-800 dark:bg-sky-500/15 dark:text-sky-200">
                              {deal.project_status.wartung.openWartungCount} Wartung{deal.project_status.wartung.openWartungCount === 1 ? '' : 'en'}
                            </span>
                          )}
                        </div>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          ID: {deal.id}
                        </span>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          Deal Status: {deal.dealstage_label || deal.dealstage || "Unbekannt"}
                        </p>
                      </div>
                    </div>

                    <div className="mb-4 space-y-2 min-w-0">
                      <div className="flex items-center justify-between rounded-2xl bg-slate-50 px-4 py-3 text-sm dark:bg-slate-900/70">
                        <span className="text-slate-600 dark:text-slate-300">Projektfortschritt</span>
                        <span className="font-semibold text-slate-900 dark:text-slate-100">
                          {deal.project_status?.overallCompletionPercent ?? 0}%
                        </span>
                      </div>
                      <div className="min-w-0 overflow-x-auto sm:overflow-visible">
                        <ProjectMilestoneTimeline
                          phases={effectiveProjectStatusPhases}
                          projektdokumentationApprovalStatus={deal.project_status?.projektdokumentationApprovalStatus || null}
                          hasProgressData={!!deal.project_status}
                          compact
                        />
                      </div>
                    </div>

                    {/* Call-to-Action */}
                    <div className="mt-4 flex items-center text-primary-600 dark:text-primary-400 text-sm font-medium">
                      <span>Projekt öffnen</span>
                      <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
                      </svg>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </section>
      </main>

    </div>
  );
};

export default ProjectsScreen;
